import React, { useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, Chip, Divider, Fade, Menu, MenuItem, Modal, Skeleton, TextField, Tooltip } from "@mui/material";
import axios from "axios";
import moment from "moment";
import { jwtDecode } from "jwt-decode";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import LinkOffRoundedIcon from "@mui/icons-material/LinkOffRounded";
import AddLinkRoundedIcon from "@mui/icons-material/AddLinkRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import NotesRoundedIcon from "@mui/icons-material/NotesRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import AddCommentRoundedIcon from "@mui/icons-material/AddCommentRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import ReplyRoundedIcon from "@mui/icons-material/ReplyRounded";
import EastRoundedIcon from "@mui/icons-material/EastRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import FlagRoundedIcon from "@mui/icons-material/FlagRounded";
import DataUsageRoundedIcon from "@mui/icons-material/DataUsageRounded";
import LocalOfferRoundedIcon from "@mui/icons-material/LocalOfferRounded";
import DirectionsRunRoundedIcon from "@mui/icons-material/DirectionsRunRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import UpdateRoundedIcon from "@mui/icons-material/UpdateRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import ManageHistoryRoundedIcon from "@mui/icons-material/ManageHistoryRounded";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import { useToast } from "../Toast";
import API_BASE_URL from "../../components/config/appConfig";
import {
  getSprintsList,
  updateIssue,
  createIssue,
  getBacklogIssues,
  setLoaderStatus,
} from "../../common/Redux/Reducer";
import { issueStatus as DEFAULT_STATUSES } from "../../utilities/Json";
import {
  DetailRow,
  EditorBlock,
  Glyph,
  LabelPicker,
  PeoplePicker,
  PersonAvatar,
  PillMenu,
  PjButton,
  RichTextBlock,
  SaveIndicator,
  getPriorityMeta,
  getStatusMeta,
  getTypeMeta,
  hasRichContent,
  personName,
} from "./TaskWorkFlowParts";
import "./taskWorkFlow.scss";

/* --------------------------------------------------------------------------
   Issue detail popup.

   Every field saves on its own through PUT /issues/updateIssue — there is no
   form-level "save". The server keys its history entry off the FIRST field in
   the payload, so each save sends exactly one field.
   -------------------------------------------------------------------------- */

const TYPE_OPTIONS = ["Story", "Bug", "Task"];
const PRIORITY_OPTIONS = ["Urgent", "High", "Medium", "Low"];
const MAX_POINTS = 8;
const POINTS_PATTERN = /^\d?(\.\d?)?$/;

const FIELD_LABELS = {
  issueType: "issue type",
  storyPointEstimate: "story points",
  linkedissues: "linked issues",
  testCases: "test cases",
};
/* long-form fields: the entry says what changed, not the before/after text */
const NO_DIFF_FIELDS = ["description", "testCases", "linkedissues", "comments"];

const CONFIRM_COPY = {
  issue: {
    title: "Delete issue",
    btnText: "Delete",
    description: "Are you sure you want to delete this issue? This action cannot be undone.",
  },
  comment: {
    title: "Delete comment",
    btnText: "Delete",
    description: "Are you sure you want to delete this comment? This action cannot be undone.",
  },
  reply: {
    title: "Delete reply",
    btnText: "Delete",
    description: "Are you sure you want to delete this reply? This action cannot be undone.",
  },
  unlink: {
    title: "Unlink issue",
    btnText: "Unlink",
    description: "The two issues will no longer be linked. You can link them again at any time.",
  },
};

const decodeUser = () => {
  try {
    const token = localStorage.getItem("accessToken");
    return token ? jwtDecode(token) : {};
  } catch (error) {
    return {};
  }
};

const firstOf = (value) => (Array.isArray(value) ? value[0] : value);

/* Reporters written by createIssue are user documents ({ _id, name, email });
   ones picked in this popup are contributors ({ employeeId, employeeName, … }).
   Both are read into the contributor shape. */
const toPerson = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const person = {
    ...raw,
    employeeId: raw.employeeId || raw._id,
    employeeName: raw.employeeName || raw.name,
    employeeEmail: raw.employeeEmail || raw.email,
  };
  return person.employeeName || person.employeeEmail ? person : null;
};

const matchMember = (person, members) => {
  if (!person) return null;
  return (
    members.find(
      (member) =>
        (person.employeeId && String(member?.employeeId) === String(person.employeeId)) ||
        (person.employeeEmail && member?.employeeEmail === person.employeeEmail)
    ) || person
  );
};

const isPopulated = (list) => Array.isArray(list) && list.every((item) => item && typeof item === "object");

/* updateIssue returns a populated issue for most fields, but a raw document for
   testCases (ids in place of linked issues and project). Keep the populated
   copies we already hold rather than regressing to ids. */
const mergeIssue = (previous = {}, next) => {
  if (!next || typeof next !== "object") return previous;
  return {
    ...previous,
    ...next,
    linkedissues: isPopulated(next.linkedissues) ? next.linkedissues : previous.linkedissues,
    project: next.project && typeof next.project === "object" ? next.project : previous.project ?? next.project,
  };
};

const fieldOf = (action = "") => (/^updated (.+)$/.exec(action) || [])[1];

const describeAction = (action = "") => {
  const field = fieldOf(action);
  return field ? `updated ${FIELD_LABELS[field] || field}` : action || "updated the issue";
};

const formatPoints = (points) => {
  const value = Number(points);
  return Number.isFinite(value) ? String(value) : "0";
};

const capitalize = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : "");

const preventBlur = (event) => event.preventDefault();

const scrollIntoViewSoon = (ref) =>
  setTimeout(() => ref.current?.scrollIntoView?.({ behavior: "smooth", block: "center" }), 60);

const ValueChip = ({ field, value, variant }) => {
  const empty = value === "" || value === null || value === undefined;
  const people = field === "assignee" || field === "reporter";
  const text = empty ? (people ? "Unassigned" : "None") : String(value);
  let lead = null;
  if (people) lead = <PersonAvatar name={empty ? "" : text} size={18} />;
  else if (!empty && field === "status") lead = <Glyph meta={getStatusMeta(text)} />;
  else if (!empty && field === "issueType") lead = <Glyph meta={getTypeMeta(text)} />;
  else if (!empty && field === "priority") lead = <Glyph meta={getPriorityMeta(text)} />;
  return (
    <span className={`pj-value pj-value--${variant}`} title={text}>
      {lead}
      <span className="pj-value__text">{text}</span>
    </span>
  );
};

const MainSkeleton = () => (
  <div className="pj-skeleton">
    <Skeleton variant="rounded" height={38} width="72%" />
    <div className="pj-skeleton__row">
      <Skeleton variant="rounded" height={34} width={112} />
      <Skeleton variant="rounded" height={34} width={112} />
      <Skeleton variant="rounded" height={34} width={112} />
    </div>
    <Skeleton variant="rounded" height={170} />
    <Skeleton variant="rounded" height={260} />
  </div>
);

const SideSkeleton = () => (
  <div className="pj-skeleton">
    <Skeleton variant="rounded" height={96} />
    <Skeleton variant="rounded" height={320} />
    <Skeleton variant="rounded" height={96} />
  </div>
);

const FeedSkeleton = ({ rows = 2 }) => (
  <div className="pj-skeleton pj-skeleton--feed">
    {Array.from({ length: rows }).map((_, index) => (
      <div className="pj-skeleton__feed" key={index}>
        <Skeleton variant="circular" width={34} height={34} />
        <div>
          <Skeleton width="40%" height={20} />
          <Skeleton width="92%" height={18} />
        </div>
      </div>
    ))}
  </div>
);

const ViewWorkFlowModalPage = (props) => {
  const {
    width = "90%",
    hideBackdrop = false,
    issueName = null,
    open = false,
    handleChangeModalOpen = () => {},
    issueStatusList,
    activeSprintName,
    setSelectedIssue = () => {},
    projectDetails,
    selectedissueId = null,
    getUpdatedBurnDownInfo = () => {},
  } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { projectSlug } = useParams();
  const toast = useToast();
  const { Projects } = useSelector((state) => state.ProjamAppSlice);
  const filterAssigneesArray = useSelector((state) => state.ProjamAppSlice.assignee);

  const selectedProject = Projects?.selectedProject || {};
  const projectData = selectedProject.selectedProjectData || {};
  const backlogId = selectedProject.selectedProjectBackLogId;
  const contributors = useMemo(
    () => (Array.isArray(selectedProject.contributors) ? selectedProject.contributors.filter(Boolean) : []),
    [selectedProject.contributors]
  );
  const me = useMemo(decodeUser, []);

  /* ------------------------------------------------------------- state */
  const [issue, setIssue] = useState({});
  const issueRef = useRef({});
  const issueId = issue?._id;
  const ready = Boolean(issueId);

  const [saveState, setSaveState] = useState("idle");
  const pendingSaves = useRef(0);
  const savedTimer = useRef(null);

  const [editingSummary, setEditingSummary] = useState(false);
  const [summaryDraft, setSummaryDraft] = useState("");
  const summaryEditing = useRef(false);

  const [editingPoints, setEditingPoints] = useState(false);
  const [pointsDraft, setPointsDraft] = useState("0");
  const pointsEditing = useRef(false);

  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState("");
  const [descSaving, setDescSaving] = useState(false);

  const [showTests, setShowTests] = useState(false);
  const [editingTests, setEditingTests] = useState(false);
  const [testsDraft, setTestsDraft] = useState("");
  const [testsSaving, setTestsSaving] = useState(false);

  const [showLinkPanel, setShowLinkPanel] = useState(false);
  const [linkMode, setLinkMode] = useState("existing");
  const [selectedLinks, setSelectedLinks] = useState([]);
  const [newLinkSummary, setNewLinkSummary] = useState("");
  const [newLinkError, setNewLinkError] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);

  const [projectLabels, setProjectLabels] = useState(projectDetails?.labels || projectData?.labels || []);

  const [activeTab, setActiveTab] = useState("comments");
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const historyFor = useRef(null);

  const [composerOpen, setComposerOpen] = useState(false);
  const [commentDraft, setCommentDraft] = useState("");
  const [commentError, setCommentError] = useState(false);
  const [commentBusy, setCommentBusy] = useState(false);

  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState("");
  const [commentEditBusy, setCommentEditBusy] = useState(false);

  const [replyingToId, setReplyingToId] = useState(null);
  const [replyDraft, setReplyDraft] = useState("");
  const [replyError, setReplyError] = useState(false);
  const [replyBusy, setReplyBusy] = useState(false);

  const [editingReplyId, setEditingReplyId] = useState(null);
  const [replyEditText, setReplyEditText] = useState("");
  const [replyEditBusy, setReplyEditBusy] = useState(false);

  const [confirm, setConfirm] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const confirmBusy = useRef(false);

  const [moreAnchor, setMoreAnchor] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  const testsRef = useRef(null);
  const linkPanelRef = useRef(null);
  const composerRef = useRef(null);

  /* ---------------------------------------------------------- helpers */
  const applyIssue = (next) => {
    issueRef.current = next;
    setIssue(next);
  };

  /* functional patch for optimistic updates and their rollbacks */
  const patchIssue = (patch) => {
    setIssue((previous) => {
      const next = { ...previous, ...patch };
      issueRef.current = next;
      return next;
    });
  };

  const refreshBoards = (assignees = filterAssigneesArray) => {
    dispatch(
      getSprintsList({
        backlogId,
        filterAssigneesArray: Array.isArray(assignees) ? assignees : [],
      })
    );
    dispatch(getBacklogIssues(`${backlogId}`));
  };

  /* Drives the header's "Saving… / All changes saved" chip. Overlapping saves
     are counted so the chip only settles once the last one lands. */
  const track = async (work) => {
    pendingSaves.current += 1;
    clearTimeout(savedTimer.current);
    setSaveState("saving");
    try {
      const result = await work();
      pendingSaves.current -= 1;
      if (!pendingSaves.current) {
        setSaveState("saved");
        savedTimer.current = setTimeout(() => setSaveState("idle"), 2600);
      }
      return result;
    } catch (error) {
      pendingSaves.current -= 1;
      setSaveState(pendingSaves.current ? "saving" : "error");
      throw error;
    }
  };

  /* Saves one field. Resolves to the merged issue, or null when it failed (the
     failure is already reported, so callers only roll back). */
  const persist = async (payload, { errorMessage = "Could not save your change", refresh = true, assignees } = {}) => {
    const id = issueRef.current?._id;
    if (!id) return null;
    try {
      const response = await track(() => dispatch(updateIssue({ issueId: id, payload })).unwrap());
      if (issueRef.current?._id !== id) return response;
      const merged = mergeIssue(issueRef.current, response);
      applyIssue(merged);
      setSelectedIssue(merged);
      if (refresh) refreshBoards(assignees);
      return merged;
    } catch (error) {
      console.error(errorMessage, error);
      toast.error(errorMessage);
      return null;
    }
  };

  /* ------------------------------------------------------------ loading */
  const loadIssueById = async (id) => {
    const hadIssue = Boolean(issueRef.current?._id);
    dispatch(setLoaderStatus({ loader: true }));
    try {
      const response = await axios(`${API_BASE_URL}/issues/getIssuesById/${id}`);
      if (response?.data) applyIssue(response.data);
    } catch (error) {
      console.error("Error loading issue:", error);
      toast.error("Could not load this issue");
      if (!hadIssue) handleChangeModalOpen();
    } finally {
      dispatch(setLoaderStatus({ loader: false }));
    }
  };

  const loadIssueByName = async (name) => {
    dispatch(setLoaderStatus({ loader: true }));
    try {
      const response = await axios(`${API_BASE_URL}/issues/getIssueByName/${name}?project=${projectSlug}`);
      if (response?.data) applyIssue(response.data);
      dispatch(setLoaderStatus({ loader: false }));
    } catch (error) {
      console.error("Error loading issue:", error);
      navigate(window?.location?.pathname);
      dispatch(setLoaderStatus({ loader: false }));
      handleChangeModalOpen();
    }
  };

  const fetchComments = async (id) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/comment/getAllcomments/${id}`);
      if (issueRef.current?._id !== id) return;
      const list = Array.isArray(response?.data?.comments) ? [...response.data.comments].reverse() : [];
      setComments(list);
    } catch (error) {
      console.error("Error loading comments:", error);
    } finally {
      if (issueRef.current?._id === id) setCommentsLoading(false);
    }
  };

  const fetchHistory = async (id) => {
    if (historyFor.current !== id) setHistoryLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/issuehistory/gethistory/${id}`);
      if (issueRef.current?._id !== id) return;
      setHistory(Array.isArray(response?.data) ? response.data : []);
      historyFor.current = id;
    } catch (error) {
      console.error("Error loading history:", error);
    } finally {
      if (issueRef.current?._id === id) setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (selectedissueId) {
      loadIssueById(selectedissueId);
    } else if (issueName) {
      loadIssueByName(issueName);
    }
    return () => clearTimeout(savedTimer.current);
    // loads once for the issue the popup was opened with
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* a different issue (first load, or a linked issue opened in place) starts clean */
  useEffect(() => {
    if (!issueId) return;
    summaryEditing.current = false;
    pointsEditing.current = false;
    setEditingSummary(false);
    setEditingPoints(false);
    setEditingDesc(false);
    setEditingTests(false);
    setShowTests(false);
    setShowLinkPanel(false);
    setSelectedLinks([]);
    setNewLinkSummary("");
    setComposerOpen(false);
    setCommentDraft("");
    setCommentError(false);
    setEditingCommentId(null);
    setReplyingToId(null);
    setEditingReplyId(null);
    setComments([]);
    setCommentsLoading(true);
    setHistory([]);
    historyFor.current = null;
    fetchComments(issueId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueId]);

  /* history follows every save while its tab is open */
  useEffect(() => {
    if (activeTab === "history" && issueId) fetchHistory(issueId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, issueId, issue?.updatedAt]);

  useEffect(() => {
    const labels = projectDetails?.labels || projectData?.labels;
    if (Array.isArray(labels)) setProjectLabels(labels);
  }, [projectDetails?.labels, projectData?.labels]);

  /* ------------------------------------------------------------ derived */
  const statusOptions = useMemo(() => {
    const source = [issueStatusList, projectData?.IssueStatus, DEFAULT_STATUSES].find(
      (list) => Array.isArray(list) && list.length
    );
    const names = [];
    (source || []).forEach((entry) => {
      const name = typeof entry === "string" ? entry : entry?.title || entry?.name;
      if (name && !names.includes(name)) names.push(name);
    });
    if (issue.status && !names.includes(issue.status)) names.push(issue.status);
    return names;
  }, [issueStatusList, projectData?.IssueStatus, issue.status]);

  const isEpic = issue.issueType === "Epic";
  const typeName = TYPE_OPTIONS.includes(issue.issueType) ? issue.issueType : "Story";
  const priorityName = PRIORITY_OPTIONS.includes(issue.priority) ? issue.priority : "Medium";
  const assigneeValue = matchMember(toPerson(firstOf(issue.assignee)), contributors);
  const reporterValue = matchMember(toPerson(firstOf(issue.reporter)), contributors);
  const linkedIssues = useMemo(
    () => (isPopulated(issue.linkedissues) ? issue.linkedissues : []),
    [issue.linkedissues]
  );
  const commentsCount = commentsLoading ? issue.comments?.length || 0 : comments.length;

  const sprintInfo = useMemo(() => {
    if (!issue.sprint) return { label: "Backlog", active: false };
    const sprints = Array.isArray(selectedProject.sprintsList) ? selectedProject.sprintsList : [];
    const found = sprints.find((sprint) => String(sprint?._id) === String(issue.sprint));
    const activeSprint = selectedProject.ActiveSprint;
    const isActive = Boolean(activeSprint?._id) && String(activeSprint._id) === String(issue.sprint);
    return {
      label: found?.name || (isActive ? activeSprint?.name : null) || activeSprintName || "Sprint",
      active: isActive || Boolean(found?.active),
    };
  }, [issue.sprint, selectedProject.sprintsList, selectedProject.ActiveSprint, activeSprintName]);

  const linkCandidates = useMemo(() => {
    const excluded = new Set([String(issueId), ...linkedIssues.map((linked) => String(linked._id))]);
    const pool = [
      ...(Array.isArray(selectedProject.ActiveSprint?.issues) ? selectedProject.ActiveSprint.issues : []),
      ...(Array.isArray(selectedProject.sprintsList)
        ? selectedProject.sprintsList.flatMap((sprint) => (Array.isArray(sprint?.issues) ? sprint.issues : []))
        : []),
    ];
    return pool.filter((candidate) => {
      if (!candidate || typeof candidate !== "object" || !candidate._id || !candidate.summary) return false;
      const key = String(candidate._id);
      if (excluded.has(key)) return false;
      excluded.add(key);
      return true;
    });
  }, [issueId, linkedIssues, selectedProject.ActiveSprint, selectedProject.sprintsList]);

  const historyItems = useMemo(
    () =>
      [...history]
        .filter(Boolean)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [history]
  );

  const hasUnsavedWork =
    editingSummary ||
    editingPoints ||
    editingDesc ||
    editingTests ||
    Boolean(editingCommentId) ||
    Boolean(editingReplyId) ||
    (composerOpen && hasRichContent(commentDraft)) ||
    (Boolean(replyingToId) && hasRichContent(replyDraft));

  const isMine = (entry) => Boolean(me?.id) && String(entry?.userId) === String(me.id);

  /* ------------------------------------------------------------ summary */
  const startSummaryEdit = () => {
    summaryEditing.current = true;
    setSummaryDraft(issue.summary || "");
    setEditingSummary(true);
  };

  const cancelSummaryEdit = () => {
    summaryEditing.current = false;
    setEditingSummary(false);
  };

  /* reachable from blur, Enter and the tick — the ref lets only the first through */
  const commitSummary = async () => {
    if (!summaryEditing.current) return;
    summaryEditing.current = false;
    setEditingSummary(false);
    const next = summaryDraft.trim();
    const previous = issue.summary || "";
    if (!next) {
      toast.info("The summary can't be empty");
      return;
    }
    if (next === previous.trim()) return;
    patchIssue({ summary: next });
    const saved = await persist({ summary: next }, { errorMessage: "Could not update the summary" });
    if (!saved) patchIssue({ summary: previous });
  };

  /* ------------------------------------------------------------- fields */
  const changeStatus = async (next) => {
    const previous = issue.status;
    if (next === previous) return;
    patchIssue({ status: next });
    const saved = await persist({ status: next }, { assignees: [], errorMessage: "Could not update the status" });
    if (!saved) {
      patchIssue({ status: previous });
      return;
    }
    if (next === "Done" || previous === "Done") getUpdatedBurnDownInfo();
  };

  const changeType = async (next) => {
    const previous = issue.issueType;
    if (next === previous) return;
    patchIssue({ issueType: next });
    const saved = await persist({ issueType: next }, { errorMessage: "Could not update the issue type" });
    if (!saved) patchIssue({ issueType: previous });
  };

  const changePriority = async (next) => {
    const previous = issue.priority;
    if (next === previous) return;
    patchIssue({ priority: next });
    const saved = await persist({ priority: next }, { errorMessage: "Could not update the priority" });
    if (!saved) patchIssue({ priority: previous });
  };

  const changeAssignee = async (person) => {
    const previous = issue.assignee;
    const next = person ? [person] : [];
    patchIssue({ assignee: next });
    const saved = await persist({ assignee: next }, { assignees: [], errorMessage: "Could not update the assignee" });
    if (!saved) patchIssue({ assignee: previous });
  };

  /* The server mails the reporter at reporter[0].email, so a contributor is
     stored with name/email alongside its employee fields. */
  const changeReporter = async (person) => {
    const previous = issue.reporter;
    const next = person ? [{ ...person, name: person.employeeName, email: person.employeeEmail }] : [];
    patchIssue({ reporter: next });
    const saved = await persist({ reporter: next }, { errorMessage: "Could not update the reporter" });
    if (!saved) patchIssue({ reporter: previous });
  };

  const changeLabel = async (name) => {
    const previous = issue.label || "";
    if (name === previous) return;
    patchIssue({ label: name });
    const saved = await persist({ label: name }, { errorMessage: "Could not update the label" });
    if (!saved) patchIssue({ label: previous });
  };

  const createLabel = async (name) => {
    const projectId = issue.project?._id || projectData?._id || localStorage.getItem("projectId");
    if (!projectId) {
      toast.error("Could not find this project");
      return false;
    }
    const nextLabels = [...(Array.isArray(projectLabels) ? projectLabels : []), { name, value: name }];
    try {
      await track(() => axios.put(`${API_BASE_URL}/projects/updateProject/${projectId}`, { labels: nextLabels }));
      setProjectLabels(nextLabels);
      return true;
    } catch (error) {
      console.error("Error creating label:", error);
      toast.error("Could not create the label");
      return false;
    }
  };

  /* -------------------------------------------------------- story points */
  const startPointsEdit = () => {
    pointsEditing.current = true;
    setPointsDraft(formatPoints(issue.storyPointEstimate ?? 0));
    setEditingPoints(true);
  };

  const cancelPointsEdit = () => {
    pointsEditing.current = false;
    setEditingPoints(false);
  };

  const onPointsChange = (event) => {
    const value = event.target.value;
    if (value === "" || (POINTS_PATTERN.test(value) && Number(value) <= MAX_POINTS)) setPointsDraft(value);
  };

  const stepPoints = (delta) =>
    setPointsDraft((previous) => {
      const next = Math.min(MAX_POINTS, Math.max(0, (Number(previous) || 0) + delta));
      return formatPoints(Math.round(next * 10) / 10);
    });

  const commitPoints = async () => {
    if (!pointsEditing.current) return;
    pointsEditing.current = false;
    setEditingPoints(false);
    const parsed = pointsDraft === "" || pointsDraft === "." ? 0 : Number(pointsDraft);
    if (!Number.isFinite(parsed)) return;
    const next = Math.min(MAX_POINTS, Math.max(0, parsed));
    const previous = Number(issue.storyPointEstimate ?? 0);
    if (next === previous) return;
    patchIssue({ storyPointEstimate: next });
    const saved = await persist({ storyPointEstimate: next }, { errorMessage: "Could not update the story points" });
    if (!saved) {
      patchIssue({ storyPointEstimate: previous });
      return;
    }
    getUpdatedBurnDownInfo();
  };

  /* ----------------------------------------------- description / tests */
  const startDescEdit = () => {
    setDescDraft(issue.description || "");
    setEditingDesc(true);
  };

  const saveDescription = async () => {
    if (descSaving) return;
    setDescSaving(true);
    const saved = await persist({ description: descDraft }, { errorMessage: "Could not update the description" });
    setDescSaving(false);
    if (saved) setEditingDesc(false);
  };

  const startTestsEdit = () => {
    setTestsDraft(issue.testCases || "");
    setShowTests(true);
    setEditingTests(true);
  };

  const openTests = () => {
    if (hasRichContent(issue.testCases)) setShowTests(true);
    else startTestsEdit();
    scrollIntoViewSoon(testsRef);
  };

  const cancelTests = () => {
    setEditingTests(false);
    if (!hasRichContent(issue.testCases)) setShowTests(false);
  };

  const saveTests = async () => {
    if (testsSaving) return;
    setTestsSaving(true);
    const saved = await persist({ testCases: testsDraft }, { errorMessage: "Could not update the test cases" });
    setTestsSaving(false);
    if (saved) setEditingTests(false);
  };

  /* ------------------------------------------------------------- linking */
  const openLinkPanel = () => {
    setLinkMode("existing");
    setShowLinkPanel(true);
    scrollIntoViewSoon(linkPanelRef);
  };

  const closeLinkPanel = () => {
    setShowLinkPanel(false);
    setSelectedLinks([]);
    setNewLinkSummary("");
    setNewLinkError(false);
  };

  const linkSelected = async () => {
    if (!selectedLinks.length || linkBusy) return;
    const count = selectedLinks.length;
    setLinkBusy(true);
    const saved = await persist(
      { linkedissues: selectedLinks.map((linked) => linked._id) },
      { refresh: false, errorMessage: "Could not link the issues" }
    );
    setLinkBusy(false);
    if (saved) {
      closeLinkPanel();
      toast.success(count === 1 ? "Issue linked" : `${count} issues linked`);
    }
  };

  const createAndLink = async () => {
    const summary = newLinkSummary.trim();
    if (!summary) {
      setNewLinkError(true);
      return;
    }
    if (linkBusy) return;
    setLinkBusy(true);
    try {
      const base = {
        project: projectData?._id,
        summary,
        backlog: backlogId,
      };
      let newIssueId;
      if (!issue.sprint) {
        const response = await track(() =>
          axios.post(`${API_BASE_URL}/issues/createBacklogIssue`, { ...base, sprint: null })
        );
        newIssueId = response?.data?._id;
      } else {
        const response = await track(() => dispatch(createIssue({ ...base, sprint: issue.sprint })).unwrap());
        newIssueId = response?._id;
      }
      if (!newIssueId) throw new Error("The new issue came back without an id");
      const saved = await persist(
        { linkedissues: [...selectedLinks.map((linked) => linked._id), newIssueId] },
        { errorMessage: "The issue was created, but linking it failed" }
      );
      if (saved) {
        closeLinkPanel();
        toast.success("Issue created and linked");
      } else {
        refreshBoards();
      }
    } catch (error) {
      console.error("Error creating linked issue:", error);
      toast.error("Could not create the issue");
    } finally {
      setLinkBusy(false);
    }
  };

  const unlinkIssue = async (linked) => {
    try {
      const response = await track(() =>
        axios.put(`${API_BASE_URL}/issues/unlinkfrom/${issue._id}?userid=${me?.id}`, { unlinkIssue: linked._id })
      );
      const nextLinked = isPopulated(response?.data?.linkedissues)
        ? response.data.linkedissues
        : linkedIssues.filter((entry) => entry._id !== linked._id);
      patchIssue({ linkedissues: nextLinked });
      toast.success("Issue unlinked");
      return true;
    } catch (error) {
      console.error("Error unlinking issue:", error);
      toast.error("Could not unlink the issue");
      return false;
    }
  };

  const changeLinkedStatus = async (linked, next) => {
    const previous = linked.status;
    if (next === previous) return;
    const setLinkedStatus = (status) =>
      setIssue((current) => {
        const updated = {
          ...current,
          linkedissues: (current.linkedissues || []).map((entry) =>
            entry?._id === linked._id ? { ...entry, status } : entry
          ),
        };
        issueRef.current = updated;
        return updated;
      });
    setLinkedStatus(next);
    try {
      await track(() => dispatch(updateIssue({ issueId: linked._id, payload: { status: next } })).unwrap());
      refreshBoards([]);
      if (next === "Done" || previous === "Done") getUpdatedBurnDownInfo();
    } catch (error) {
      console.error("Error updating linked issue:", error);
      setLinkedStatus(previous);
      toast.error("Could not update the linked issue");
    }
  };

  const openLinkedIssue = (linked) => {
    setSelectedIssue(linked);
    loadIssueById(linked._id);
  };

  /* ------------------------------------------------------------ comments */
  const openComposer = () => {
    setActiveTab("comments");
    setComposerOpen(true);
    scrollIntoViewSoon(composerRef);
  };

  const cancelComposer = () => {
    setComposerOpen(false);
    setCommentDraft("");
    setCommentError(false);
  };

  const submitComment = async () => {
    if (commentBusy) return;
    if (!hasRichContent(commentDraft)) {
      setCommentError(true);
      return;
    }
    setCommentBusy(true);
    try {
      await track(() =>
        axios.post(`${API_BASE_URL}/issues/addcomment/${issue._id}`, { userId: me?.id, comment: commentDraft })
      );
      cancelComposer();
      setActiveTab("comments");
      await fetchComments(issue._id);
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Could not post your comment");
    } finally {
      setCommentBusy(false);
    }
  };

  const startCommentEdit = (comment) => {
    setEditingCommentId(comment._id);
    setEditingCommentText(comment.comment || "");
  };

  const saveCommentEdit = async (comment) => {
    if (commentEditBusy) return;
    if (!hasRichContent(editingCommentText)) {
      toast.info("A comment can't be empty");
      return;
    }
    setCommentEditBusy(true);
    try {
      await track(() =>
        axios.put(`${API_BASE_URL}/comment/update/${comment._id}`, {
          userId: me?.id,
          comment: editingCommentText,
          issueId: issue._id,
        })
      );
      setEditingCommentId(null);
      setEditingCommentText("");
      await fetchComments(issue._id);
    } catch (error) {
      console.error("Error updating comment:", error);
      toast.error("Could not update the comment");
    } finally {
      setCommentEditBusy(false);
    }
  };

  const deleteComment = async (comment) => {
    try {
      await track(() => axios.delete(`${API_BASE_URL}/comment/delete/${comment._id}?issueId=${issue._id}`));
      await fetchComments(issue._id);
      toast.success("Comment deleted");
      return true;
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast.error("Could not delete the comment");
      return false;
    }
  };

  const toggleReply = (commentId) => {
    setReplyError(false);
    if (replyingToId === commentId) {
      setReplyingToId(null);
      return;
    }
    setReplyDraft("");
    setReplyingToId(commentId);
  };

  const submitReply = async (comment) => {
    if (replyBusy) return;
    if (!hasRichContent(replyDraft)) {
      setReplyError(true);
      return;
    }
    setReplyBusy(true);
    try {
      await track(() =>
        axios.post(
          `${API_BASE_URL}/comment/reply`,
          {
            userId: me?.id,
            name: me?.name,
            email: me?.email,
            comment: `<span class="mention">@${comment.name} </span> ${replyDraft}`,
            parentCmnt: comment._id,
            issueId: issue._id,
          },
          { headers: { "Content-Type": "application/json" } }
        )
      );
      setReplyingToId(null);
      setReplyDraft("");
      setReplyError(false);
      await fetchComments(issue._id);
    } catch (error) {
      console.error("Error adding reply:", error);
      toast.error("Could not post your reply");
    } finally {
      setReplyBusy(false);
    }
  };

  const startReplyEdit = (reply) => {
    setEditingReplyId(reply._id);
    setReplyEditText(reply.comment || "");
  };

  const saveReplyEdit = async () => {
    if (replyEditBusy || !editingReplyId) return;
    if (!hasRichContent(replyEditText)) {
      toast.info("A reply can't be empty");
      return;
    }
    setReplyEditBusy(true);
    try {
      await track(() =>
        axios.put(
          `${API_BASE_URL}/comment/updateReply/${editingReplyId}`,
          { comment: replyEditText, issueId: issue._id },
          { headers: { "Content-Type": "application/json" } }
        )
      );
      setEditingReplyId(null);
      setReplyEditText("");
      await fetchComments(issue._id);
    } catch (error) {
      console.error("Error updating reply:", error);
      toast.error("Could not update the reply");
    } finally {
      setReplyEditBusy(false);
    }
  };

  const deleteReply = async (reply) => {
    try {
      await track(() => axios.delete(`${API_BASE_URL}/comment/deleteReply/${reply._id}?issueId=${issue._id}`));
      await fetchComments(issue._id);
      toast.success("Reply deleted");
      return true;
    } catch (error) {
      console.error("Error deleting reply:", error);
      toast.error("Could not delete the reply");
      return false;
    }
  };

  /* ------------------------------------------------------ issue actions */
  const closeModal = () => {
    handleChangeModalOpen();
    if (issueName && selectedProject.projectSlug) {
      navigate(window?.location?.pathname);
    }
  };

  /* Esc / backdrop must not throw away an open editor; the close button still
     closes unconditionally. */
  const handleModalClose = (event, reason) => {
    if ((reason === "escapeKeyDown" || reason === "backdropClick") && hasUnsavedWork) return;
    closeModal();
  };

  const deleteIssue = async () => {
    try {
      await track(() => axios.delete(`${API_BASE_URL}/issues/deleteIssue/${issue._id}`));
      refreshBoards();
      toast.success("Issue deleted");
      setConfirmOpen(false);
      closeModal();
      return true;
    } catch (error) {
      console.error("Error deleting issue:", error);
      toast.error("Could not delete the issue");
      return false;
    }
  };

  const copyText = async (text, success) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(success);
    } catch (error) {
      toast.error("Could not copy to the clipboard");
    }
  };

  const copyLink = () => {
    const url = new URL(window.location.href);
    if (issue.name) url.searchParams.set("selectedIssue", issue.name);
    copyText(url.toString(), "Link copied");
  };

  const copyKey = () => {
    if (issue.name) copyText(issue.name, `${issue.name} copied`);
  };

  const askConfirm = (kind, target = null) => {
    setConfirm({ kind, target });
    setConfirmOpen(true);
  };

  const runConfirm = async () => {
    if (!confirm || confirmBusy.current) return;
    confirmBusy.current = true;
    const actions = {
      issue: () => deleteIssue(),
      comment: () => deleteComment(confirm.target),
      reply: () => deleteReply(confirm.target),
      unlink: () => unlinkIssue(confirm.target),
    };
    try {
      const done = await actions[confirm.kind]?.();
      if (done) setConfirmOpen(false);
    } finally {
      confirmBusy.current = false;
    }
  };

  const onRichClick = (event) => {
    const image = event.target.closest?.("img");
    if (image) setPreviewImage(image.src);
  };

  const confirmCopy = CONFIRM_COPY[confirm?.kind] || CONFIRM_COPY.issue;
  const showTestsSection = showTests || editingTests || hasRichContent(issue.testCases);

  /* ------------------------------------------------------------- render */
  const renderSummary = () =>
    editingSummary ? (
      <div className="pj-summary-edit">
        <TextField
          className="pj-input pj-summary-input"
          multiline
          fullWidth
          autoFocus
          value={summaryDraft}
          onChange={(event) => setSummaryDraft(event.target.value)}
          onBlur={commitSummary}
          onFocus={(event) => {
            const length = event.target.value.length;
            event.target.setSelectionRange(length, length);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              commitSummary();
            } else if (event.key === "Escape") {
              event.preventDefault();
              event.stopPropagation();
              cancelSummaryEdit();
            }
          }}
          inputProps={{ maxLength: 255, "aria-label": "Issue summary" }}
        />
        <div className="pj-summary-edit__actions">
          <span className="pj-hint">
            <kbd>Enter</kbd> to save · <kbd>Esc</kbd> to cancel
          </span>
          <button type="button" className="pj-round-btn" onMouseDown={preventBlur} onClick={cancelSummaryEdit} aria-label="Cancel">
            <CloseRoundedIcon />
          </button>
          <button
            type="button"
            className="pj-round-btn pj-round-btn--primary"
            onMouseDown={preventBlur}
            onClick={commitSummary}
            aria-label="Save summary"
          >
            <CheckRoundedIcon />
          </button>
        </div>
      </div>
    ) : (
      <h2
        className="pj-summary pj-anim-host"
        role="button"
        tabIndex={0}
        title="Click to edit"
        onClick={startSummaryEdit}
        onKeyDown={(event) => {
          if (event.key === "Enter") startSummaryEdit();
        }}
      >
        {issue.summary}
        <span className="pj-summary__edit pj-motion--wiggle" aria-hidden="true">
          <EditRoundedIcon />
        </span>
      </h2>
    );

  const renderLinkPanel = () => (
    <section className="pj-card pj-card--focus" ref={linkPanelRef}>
      <header className="pj-card__head">
        <span className="pj-card__icon pj-tone--violet pj-motion--tilt">
          <AddLinkRoundedIcon />
        </span>
        <h3 className="pj-card__title">Link issues</h3>
        <div className="pj-seg pj-card__action" data-active={linkMode === "existing" ? "first" : "second"}>
          <span className="pj-seg__thumb" aria-hidden="true" />
          <button type="button" className={linkMode === "existing" ? "is-active" : ""} onClick={() => setLinkMode("existing")}>
            Existing
          </button>
          <button type="button" className={linkMode === "create" ? "is-active" : ""} onClick={() => setLinkMode("create")}>
            Create new
          </button>
        </div>
      </header>

      {linkMode === "existing" ? (
        <Autocomplete
          multiple
          className="pj-field pj-field--multi"
          options={linkCandidates}
          value={selectedLinks}
          onChange={(event, next) => setSelectedLinks(next)}
          disableCloseOnSelect
          openOnFocus
          popupIcon={<ExpandMoreRoundedIcon />}
          slotProps={{ paper: { className: "pj-issue-pop" }, popper: { className: "pj-issue-popper" } }}
          noOptionsText="No other issues available to link"
          getOptionLabel={(option) => `${option.name ? `${option.name} ` : ""}${option.summary || ""}`}
          isOptionEqualToValue={(option, selected) => option._id === selected._id}
          renderOption={(optionProps, option, { selected }) => {
            const { key, ...rest } = optionProps;
            return (
              <li key={key} {...rest} className={`${rest.className || ""} pj-opt`}>
                <Glyph meta={getTypeMeta(option.issueType)} />
                {option.name ? <span className="pj-opt__key">{option.name}</span> : null}
                <span className="pj-opt__title pj-opt__title--grow">{option.summary}</span>
                {selected ? <CheckRoundedIcon className="pj-opt__check" /> : null}
              </li>
            );
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return <Chip key={key} {...tagProps} size="small" label={option.name || option.summary} />;
            })
          }
          renderInput={(params) => (
            <TextField {...params} autoFocus placeholder={selectedLinks.length ? "" : "Search issues to link…"} />
          )}
        />
      ) : (
        <TextField
          className="pj-input"
          fullWidth
          autoFocus
          placeholder="What needs to be done?"
          value={newLinkSummary}
          error={newLinkError}
          helperText={
            newLinkError
              ? "Enter a summary for the new issue"
              : issue.sprint
                ? "It will be created in this issue's sprint"
                : "It will be created in the backlog"
          }
          onChange={(event) => {
            setNewLinkSummary(event.target.value);
            if (newLinkError) setNewLinkError(false);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              createAndLink();
            }
          }}
        />
      )}

      <div className="pj-editor-actions">
        <PjButton variant="ghost" size="sm" onClick={closeLinkPanel} disabled={linkBusy}>
          Cancel
        </PjButton>
        {linkMode === "existing" ? (
          <PjButton size="sm" busy={linkBusy} disabled={!selectedLinks.length} onClick={linkSelected} icon={<LinkRoundedIcon />}>
            {linkBusy ? "Linking…" : selectedLinks.length > 1 ? `Link ${selectedLinks.length} issues` : "Link issue"}
          </PjButton>
        ) : (
          <PjButton size="sm" busy={linkBusy} onClick={createAndLink} icon={<AddRoundedIcon />}>
            {linkBusy ? "Creating…" : "Create & link"}
          </PjButton>
        )}
      </div>
    </section>
  );

  const renderLinkedIssues = () => (
    <section className="pj-card" style={{ "--d": "90ms" }}>
      <header className="pj-card__head">
        <span className="pj-card__icon pj-tone--teal pj-motion--bob">
          <AccountTreeRoundedIcon />
        </span>
        <h3 className="pj-card__title">Linked issues</h3>
        <span className="pj-count">{linkedIssues.length}</span>
        <button type="button" className="pj-link-btn pj-card__action" onClick={openLinkPanel}>
          <AddRoundedIcon />
          Add
        </button>
      </header>
      <div className="pj-linked-list">
        {linkedIssues.map((linked, index) => {
          const owner = firstOf(linked.assignee);
          return (
            <div className="pj-linked pj-anim-host" key={linked._id || index} style={{ "--d": `${index * 40}ms` }}>
              <button type="button" className="pj-linked__main" onClick={() => openLinkedIssue(linked)} title="Open issue">
                <Glyph meta={getTypeMeta(linked.issueType)} />
                <span className="pj-linked__key">{linked.name}</span>
                <span className="pj-linked__summary">{capitalize(linked.summary)}</span>
              </button>
              <Tooltip title={linked.priority || "Medium"}>
                <span className="pj-linked__priority">
                  <Glyph meta={getPriorityMeta(linked.priority)} />
                </span>
              </Tooltip>
              <PersonAvatar name={personName(toPerson(owner))} size={24} />
              <PillMenu
                variant="mini"
                value={linked.status}
                options={statusOptions}
                getMeta={getStatusMeta}
                onSelect={(next) => changeLinkedStatus(linked, next)}
                ariaLabel={`Change status of ${linked.name}`}
              />
              <Tooltip title="Unlink">
                <button type="button" className="pj-icon-btn pj-icon-btn--danger" onClick={() => askConfirm("unlink", linked)} aria-label="Unlink issue">
                  <LinkOffRoundedIcon />
                </button>
              </Tooltip>
            </div>
          );
        })}
      </div>
    </section>
  );

  const renderComment = (comment, index) => {
    const replies = [...(comment?.replies || [])]
      .filter(Boolean)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return (
      <article className="pj-comment" key={comment._id || index} style={{ "--d": `${Math.min(index, 8) * 35}ms` }}>
        <PersonAvatar name={comment.name} size={34} />
        <div className="pj-comment__body">
          <div className="pj-comment__meta">
            <span className="pj-comment__name">{comment.name}</span>
            <Tooltip title={moment(comment.createdAt).format("DD MMM YYYY, HH:mm")}>
              <span className="pj-comment__time">{moment(comment.createdAt).fromNow()}</span>
            </Tooltip>
          </div>

          {editingCommentId === comment._id ? (
            <EditorBlock
              value={editingCommentText}
              onChange={setEditingCommentText}
              onSave={() => saveCommentEdit(comment)}
              onCancel={() => setEditingCommentId(null)}
              saving={commentEditBusy}
              autoFocus
            />
          ) : (
            <>
              <div className="pj-rich" onClick={onRichClick} dangerouslySetInnerHTML={{ __html: comment.comment }} />
              <div className="pj-comment__actions">
                <button type="button" className={`pj-link-btn ${replyingToId === comment._id ? "is-active" : ""}`} onClick={() => toggleReply(comment._id)}>
                  <ReplyRoundedIcon />
                  Reply
                </button>
                {isMine(comment) ? (
                  <>
                    <button type="button" className="pj-link-btn" onClick={() => startCommentEdit(comment)}>
                      <EditRoundedIcon />
                      Edit
                    </button>
                    <button type="button" className="pj-link-btn is-danger" onClick={() => askConfirm("comment", comment)}>
                      <DeleteOutlineRoundedIcon />
                      Delete
                    </button>
                  </>
                ) : null}
              </div>
            </>
          )}

          {replyingToId === comment._id ? (
            <div className="pj-reply-composer">
              <PersonAvatar name={me?.name || me?.email} size={28} />
              <div className="pj-composer__body">
                <span className="pj-replying-to">
                  Replying to <strong>@{comment.name}</strong>
                </span>
                <EditorBlock
                  value={replyDraft}
                  onChange={(value) => {
                    setReplyDraft(value);
                    if (replyError) setReplyError(false);
                  }}
                  onSave={() => submitReply(comment)}
                  onCancel={() => toggleReply(comment._id)}
                  saving={replyBusy}
                  saveLabel="Reply"
                  error={replyError ? "Write a reply before sending" : ""}
                  autoFocus
                />
              </div>
            </div>
          ) : null}

          {replies.length ? (
            <div className="pj-replies">
              {replies.map((reply) => (
                <div className="pj-reply" key={reply._id}>
                  <PersonAvatar name={reply.name} size={28} />
                  <div className="pj-comment__body">
                    <div className="pj-comment__meta">
                      <span className="pj-comment__name">{reply.name}</span>
                      <Tooltip title={moment(reply.createdAt).format("DD MMM YYYY, HH:mm")}>
                        <span className="pj-comment__time">{moment(reply.createdAt).fromNow()}</span>
                      </Tooltip>
                    </div>
                    {editingReplyId === reply._id ? (
                      <EditorBlock
                        value={replyEditText}
                        onChange={setReplyEditText}
                        onSave={saveReplyEdit}
                        onCancel={() => setEditingReplyId(null)}
                        saving={replyEditBusy}
                        saveLabel="Update"
                        autoFocus
                      />
                    ) : (
                      <>
                        <div className="pj-rich" onClick={onRichClick} dangerouslySetInnerHTML={{ __html: reply.comment }} />
                        {isMine(reply) ? (
                          <div className="pj-comment__actions">
                            <button type="button" className="pj-link-btn" onClick={() => startReplyEdit(reply)}>
                              <EditRoundedIcon />
                              Edit
                            </button>
                            <button type="button" className="pj-link-btn is-danger" onClick={() => askConfirm("reply", reply)}>
                              <DeleteOutlineRoundedIcon />
                              Delete
                            </button>
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </article>
    );
  };

  const renderHistory = () => {
    if (historyLoading) return <FeedSkeleton rows={3} />;
    if (!historyItems.length) {
      return (
        <div className="pj-empty">
          <span className="pj-empty__icon pj-motion--spin-once">
            <ManageHistoryRoundedIcon />
          </span>
          No history yet
        </div>
      );
    }
    return (
      <ol className="pj-timeline">
        {historyItems.map((item, index) => {
          const field = fieldOf(item.action);
          const created = (item.action || "").includes("created");
          const showDiff = !created && field && !NO_DIFF_FIELDS.includes(field);
          return (
            <li className="pj-event pj-anim-host" key={item._id || index} style={{ "--d": `${Math.min(index, 10) * 30}ms` }}>
              <PersonAvatar name={item.name} size={30} />
              <div className="pj-event__body">
                <div className="pj-event__line">
                  <strong>{item.name}</strong>
                  <span>{describeAction(item.action)}</span>
                  <Tooltip title={moment(item.createdAt).format("DD MMM YYYY, HH:mm")}>
                    <time>{moment(item.createdAt).fromNow()}</time>
                  </Tooltip>
                </div>
                {showDiff ? (
                  <div className="pj-change">
                    <ValueChip field={field} value={item.previous} variant="before" />
                    <span className="pj-change__arrow pj-motion--nudge-right">
                      <EastRoundedIcon />
                    </span>
                    <ValueChip field={field} value={item.latest} variant="after" />
                  </div>
                ) : null}
                {created && item.previous ? (
                  <div className="pj-change">
                    <ValueChip field={field} value={item.previous} variant="after" />
                  </div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>
    );
  };

  const renderActivity = () => (
    <section className="pj-card pj-activity" style={{ "--d": "140ms" }}>
      <header className="pj-card__head">
        <span className="pj-card__icon pj-tone--green pj-motion--bob">
          <ForumRoundedIcon />
        </span>
        <h3 className="pj-card__title">Activity</h3>
        <div className="pj-seg pj-card__action" data-active={activeTab === "comments" ? "first" : "second"}>
          <span className="pj-seg__thumb" aria-hidden="true" />
          <button type="button" className={activeTab === "comments" ? "is-active" : ""} onClick={() => setActiveTab("comments")}>
            <ChatBubbleOutlineRoundedIcon />
            Comments
            <span className="pj-count pj-count--tab">{commentsCount}</span>
          </button>
          <button type="button" className={activeTab === "history" ? "is-active" : ""} onClick={() => setActiveTab("history")}>
            <HistoryRoundedIcon />
            History
          </button>
        </div>
      </header>

      {activeTab === "comments" ? (
        <div className="pj-tab-panel" key="comments">
          <div className="pj-composer" ref={composerRef}>
            <PersonAvatar name={me?.name || me?.email} size={34} />
            <div className="pj-composer__body">
              {composerOpen ? (
                <EditorBlock
                  value={commentDraft}
                  onChange={(value) => {
                    setCommentDraft(value);
                    if (commentError) setCommentError(false);
                  }}
                  onSave={submitComment}
                  onCancel={cancelComposer}
                  saving={commentBusy}
                  saveLabel="Comment"
                  error={commentError ? "Write something before posting" : ""}
                  autoFocus
                />
              ) : (
                <button type="button" className="pj-composer__fake pj-anim-host" onClick={() => setComposerOpen(true)}>
                  <span className="pj-motion--wiggle">
                    <AddCommentRoundedIcon />
                  </span>
                  Add a comment…
                </button>
              )}
            </div>
          </div>

          {commentsLoading ? (
            <FeedSkeleton />
          ) : comments.length ? (
            <div className="pj-comments">{comments.map(renderComment)}</div>
          ) : (
            <div className="pj-empty">
              <span className="pj-empty__icon pj-motion--bob">
                <ForumOutlinedIcon />
              </span>
              No comments yet — start the conversation
            </div>
          )}
        </div>
      ) : (
        <div className="pj-tab-panel" key="history">
          {renderHistory()}
        </div>
      )}
    </section>
  );

  const renderMain = () => (
    <>
      {renderSummary()}

      <div className="pj-quick">
        <button type="button" className={`pj-chip-btn pj-anim-host ${showLinkPanel ? "is-active" : ""}`} onClick={openLinkPanel}>
          <span className="pj-motion--tilt">
            <AddLinkRoundedIcon />
          </span>
          Link issue
        </button>
        <button type="button" className={`pj-chip-btn pj-anim-host ${showTestsSection ? "is-active" : ""}`} onClick={openTests}>
          <span className="pj-motion--bob">
            <FactCheckRoundedIcon />
          </span>
          Test cases
        </button>
        <button type="button" className="pj-chip-btn pj-anim-host" onClick={openComposer}>
          <span className="pj-motion--wiggle">
            <AddCommentRoundedIcon />
          </span>
          Comment
        </button>
      </div>

      <section className="pj-card pj-anim-host" style={{ "--d": "40ms" }}>
        <header className="pj-card__head">
          <span className="pj-card__icon pj-tone--blue pj-motion--wiggle">
            <NotesRoundedIcon />
          </span>
          <h3 className="pj-card__title">Description</h3>
          {!editingDesc && hasRichContent(issue.description) ? (
            <button type="button" className="pj-link-btn pj-card__action" onClick={startDescEdit}>
              <EditRoundedIcon />
              Edit
            </button>
          ) : null}
        </header>
        {editingDesc ? (
          <EditorBlock
            value={descDraft}
            onChange={setDescDraft}
            onSave={saveDescription}
            onCancel={() => setEditingDesc(false)}
            saving={descSaving}
            autoFocus
          />
        ) : (
          <RichTextBlock
            html={issue.description}
            placeholder="Add a description…"
            onEdit={startDescEdit}
            onImage={setPreviewImage}
            icon={<EditNoteRoundedIcon />}
          />
        )}
      </section>

      {showTestsSection ? (
        <section className="pj-card pj-anim-host" style={{ "--d": "60ms" }} ref={testsRef}>
          <header className="pj-card__head">
            <span className="pj-card__icon pj-tone--amber pj-motion--bob">
              <FactCheckRoundedIcon />
            </span>
            <h3 className="pj-card__title">Test cases</h3>
            {!editingTests && hasRichContent(issue.testCases) ? (
              <button type="button" className="pj-link-btn pj-card__action" onClick={startTestsEdit}>
                <EditRoundedIcon />
                Edit
              </button>
            ) : null}
          </header>
          {editingTests ? (
            <EditorBlock value={testsDraft} onChange={setTestsDraft} onSave={saveTests} onCancel={cancelTests} saving={testsSaving} autoFocus />
          ) : (
            <RichTextBlock
              html={issue.testCases}
              placeholder="Add test cases…"
              onEdit={startTestsEdit}
              onImage={setPreviewImage}
              icon={<FactCheckRoundedIcon />}
            />
          )}
        </section>
      ) : null}

      {showLinkPanel ? renderLinkPanel() : null}
      {linkedIssues.length ? renderLinkedIssues() : null}
      {renderActivity()}
    </>
  );

  const renderSide = () => (
    <>
      <section className="pj-side-card pj-status-card" style={{ "--d": "30ms" }}>
        <span className="pj-eyebrow">Status</span>
        <div className="pj-status-row">
          <PillMenu variant="hero" value={issue.status} options={statusOptions} getMeta={getStatusMeta} onSelect={changeStatus} ariaLabel="Change status" />
          {isEpic ? (
            <span className="pj-select pj-select--static pj-tone--violet pj-anim-host">
              <Glyph meta={getTypeMeta("Epic")} />
              <span className="pj-select__label">Epic</span>
            </span>
          ) : (
            <PillMenu value={typeName} options={TYPE_OPTIONS} getMeta={getTypeMeta} onSelect={changeType} ariaLabel="Change issue type" />
          )}
        </div>
      </section>

      <section className="pj-side-card" style={{ "--d": "80ms" }}>
        <h3 className="pj-side-card__title">
          <TuneRoundedIcon />
          Details
        </h3>
        <DetailRow icon={<PersonRoundedIcon />} label="Assignee">
          <PeoplePicker value={assigneeValue} members={contributors} onSelect={changeAssignee} ariaLabel="Assignee" />
        </DetailRow>
        <DetailRow icon={<CampaignRoundedIcon />} motion="wiggle" label="Reporter">
          <PeoplePicker value={reporterValue} members={contributors} onSelect={changeReporter} ariaLabel="Reporter" />
        </DetailRow>
        <DetailRow icon={<FlagRoundedIcon />} motion="wave" label="Priority">
          <PillMenu variant="field" value={priorityName} options={PRIORITY_OPTIONS} getMeta={getPriorityMeta} onSelect={changePriority} ariaLabel="Change priority" />
        </DetailRow>
        <DetailRow icon={<DataUsageRoundedIcon />} motion="spin-once" label="Story points">
          {editingPoints ? (
            <div className="pj-points-edit">
              <button type="button" className="pj-step" onMouseDown={preventBlur} onClick={() => stepPoints(-1)} aria-label="Fewer points">
                <RemoveRoundedIcon />
              </button>
              <input
                autoFocus
                inputMode="decimal"
                value={pointsDraft}
                onChange={onPointsChange}
                onBlur={commitPoints}
                onFocus={(event) => event.target.select()}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    commitPoints();
                  } else if (event.key === "Escape") {
                    event.preventDefault();
                    event.stopPropagation();
                    cancelPointsEdit();
                  } else if (event.key === "ArrowUp" || event.key === "ArrowDown") {
                    event.preventDefault();
                    stepPoints(event.key === "ArrowUp" ? 1 : -1);
                  }
                }}
                aria-label="Story points"
              />
              <button type="button" className="pj-step" onMouseDown={preventBlur} onClick={() => stepPoints(1)} aria-label="More points">
                <AddRoundedIcon />
              </button>
            </div>
          ) : (
            <button type="button" className="pj-points" onClick={startPointsEdit} key={formatPoints(issue.storyPointEstimate)} title={`Up to ${MAX_POINTS} points`}>
              {formatPoints(issue.storyPointEstimate ?? 0)}
              <span>pts</span>
            </button>
          )}
        </DetailRow>
        <DetailRow icon={<LocalOfferRoundedIcon />} motion="wiggle" label="Label">
          <LabelPicker value={issue.label || ""} labels={projectLabels} onSelect={changeLabel} onCreate={createLabel} />
        </DetailRow>
        <DetailRow icon={<DirectionsRunRoundedIcon />} motion="nudge-right" label="Sprint">
          <span className={`pj-sprint ${sprintInfo.active ? "is-active" : ""}`}>
            <span className="pj-sprint__dot" />
            {sprintInfo.label}
          </span>
        </DetailRow>
      </section>

      <section className="pj-side-card" style={{ "--d": "130ms" }}>
        <DetailRow icon={<EventRoundedIcon />} label="Created">
          <span className="pj-date">{moment(issue.createdAt).format("DD MMM YYYY, HH:mm")}</span>
        </DetailRow>
        <DetailRow icon={<UpdateRoundedIcon />} motion="spin-once" label="Updated">
          <Tooltip title={moment(issue.updatedAt).format("DD MMM YYYY, HH:mm")}>
            <span className="pj-date">{moment(issue.updatedAt).fromNow()}</span>
          </Tooltip>
        </DetailRow>
      </section>

      <PjButton variant="danger" className="pj-ibtn--block" icon={<DeleteOutlineRoundedIcon />} onClick={() => askConfirm("issue")}>
        Delete issue
      </PjButton>
    </>
  );

  return (
    <div>
      <Modal
        open={open}
        hideBackdrop={hideBackdrop}
        onClose={handleModalClose}
        // CKEditor's link / image balloons render outside the dialog and need focus
        disableEnforceFocus
        slotProps={{ backdrop: { className: "pj-issue-backdrop" } }}
      >
        <div className="pj-issue-stage">
          <div className="pj-issue" style={{ width }} role="dialog" aria-label={issue.name ? `Issue ${issue.name}` : "Issue details"}>
            <header className="pj-issue__head">
              <span className={`pj-type-tile pj-anim-host ${ready ? "" : "is-loading"}`}>
                {ready ? <Glyph meta={getTypeMeta(isEpic ? "Epic" : typeName)} key={issue.issueType} /> : null}
              </span>
              <div className="pj-crumbs">
                <span className="pj-crumbs__project">{issue.project?.title || projectData?.title || "Issue"}</span>
                {ready ? (
                  <Tooltip title="Copy issue key">
                    <button type="button" className="pj-crumbs__key" onClick={copyKey}>
                      {issue.name}
                      <ContentCopyRoundedIcon />
                    </button>
                  </Tooltip>
                ) : (
                  <span className="pj-crumbs__key is-placeholder">Loading…</span>
                )}
              </div>
              <SaveIndicator state={saveState} />
              <div className="pj-head-actions">
                <Tooltip title="Copy link">
                  <button type="button" className="pj-head-btn pj-anim-host" onClick={copyLink} disabled={!ready} aria-label="Copy link">
                    <span className="pj-motion--tilt">
                      <LinkRoundedIcon />
                    </span>
                  </button>
                </Tooltip>
                <Tooltip title="More actions">
                  <button type="button" className="pj-head-btn" onClick={(event) => setMoreAnchor(event.currentTarget)} disabled={!ready} aria-label="More actions">
                    <MoreHorizRoundedIcon />
                  </button>
                </Tooltip>
                <Tooltip title="Close">
                  <button type="button" className="pj-head-btn pj-head-btn--close" onClick={closeModal} aria-label="Close">
                    <CloseRoundedIcon />
                  </button>
                </Tooltip>
              </div>
            </header>

            <div className="pj-issue__body">
              <main className="pj-issue__main">{ready ? renderMain() : <MainSkeleton />}</main>
              <aside className="pj-issue__side">{ready ? renderSide() : <SideSkeleton />}</aside>
            </div>
          </div>
        </div>
      </Modal>

      <Menu
        anchorEl={moreAnchor}
        open={Boolean(moreAnchor)}
        onClose={() => setMoreAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { className: "pj-issue-menu" } }}
      >
        <MenuItem
          className="pj-issue-menu__item pj-anim-host"
          style={{ "--i": 0 }}
          onClick={() => {
            setMoreAnchor(null);
            copyLink();
          }}
        >
          <span className="pj-menu-icon pj-motion--tilt">
            <LinkRoundedIcon />
          </span>
          Copy link
        </MenuItem>
        <MenuItem
          className="pj-issue-menu__item pj-anim-host"
          style={{ "--i": 1 }}
          onClick={() => {
            setMoreAnchor(null);
            copyKey();
          }}
        >
          <span className="pj-menu-icon pj-motion--pop">
            <ContentCopyRoundedIcon />
          </span>
          Copy issue key
        </MenuItem>
        <MenuItem
          className="pj-issue-menu__item pj-anim-host"
          style={{ "--i": 2 }}
          onClick={() => {
            setMoreAnchor(null);
            openLinkPanel();
          }}
        >
          <span className="pj-menu-icon pj-motion--tilt">
            <AddLinkRoundedIcon />
          </span>
          Link issue
        </MenuItem>
        <Divider />
        <MenuItem
          className="pj-issue-menu__item pj-issue-menu__item--danger pj-anim-host"
          style={{ "--i": 3 }}
          onClick={() => {
            setMoreAnchor(null);
            askConfirm("issue");
          }}
        >
          <span className="pj-menu-icon pj-motion--wiggle">
            <DeleteOutlineRoundedIcon />
          </span>
          Delete issue
        </MenuItem>
      </Menu>

      <Modal open={Boolean(previewImage)} onClose={() => setPreviewImage(null)} slotProps={{ backdrop: { className: "pj-issue-backdrop" } }}>
        <Fade in={Boolean(previewImage)}>
          <div className="pj-lightbox" onClick={() => setPreviewImage(null)}>
            <button type="button" className="pj-head-btn pj-head-btn--close pj-lightbox__close" onClick={() => setPreviewImage(null)} aria-label="Close preview">
              <CloseRoundedIcon />
            </button>
            {previewImage ? <img src={previewImage} alt="Attachment preview" onClick={(event) => event.stopPropagation()} /> : null}
          </div>
        </Fade>
      </Modal>

      <DeleteConfirmationModal
        open={confirmOpen}
        handleClose={() => setConfirmOpen(false)}
        handleConfirm={runConfirm}
        btnText={confirmCopy.btnText}
        title={confirmCopy.title}
        description={confirmCopy.description}
      />
    </div>
  );
};

export default ViewWorkFlowModalPage;
